// src/screens/FinanceReports.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../css/FinanceReports.css";
import * as XLSX from "xlsx";
import useIdleLogout from "../hooks/useIdleLogout";

/**
 * .env REQUIRED:
 * REACT_APP_TRANSPORT_GST_REPORT_API_URL=http://192.168.131.171:4000/finance/transport/gst-report
 * REACT_APP_TRANSPORT_FINANCE_SUMMARY_API_URL=http://192.168.131.171:4000/finance/transport/finance-summary
 */
const API_GST_REPORT = (process.env.REACT_APP_TRANSPORT_GST_REPORT_API_URL || "").trim();
const API_FIN_SUMMARY = (process.env.REACT_APP_TRANSPORT_FINANCE_SUMMARY_API_URL || "").trim();

const DISPLAY_LIMIT = 20;

/* ===== helpers ===== */
const pad2 = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const centsToNu = (c) => Number(c || 0) / 100;

const money = (n) =>
  `BTN. ${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function getDefaultRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: ymd(from), to: ymd(to) };
}

function safeDateMs(v) {
  if (!v) return 0;
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function normalizeFinanceRows(raw) {
  // { ok:true, data:{ rows:[{id, ride_id, platform_fee_cents, gst_cents, total_payable_cents, driver_payout_cents, platform_fee_rule_id, tax_rule_id, created_at}], count } }
  const arr =
    (Array.isArray(raw?.data?.rows) && raw.data.rows) ||
    (Array.isArray(raw?.rows) && raw.rows) ||
    (Array.isArray(raw?.data) && raw.data) ||
    (Array.isArray(raw) && raw) ||
    [];

  return arr.map((r, i) => {
    const createdAt = r.created_at || r.createdAt || r.time || r.timestamp || "";
    const createdAtMs = safeDateMs(createdAt);

    return {
      id: r.id ?? i + 1,
      rideId: r.ride_id ?? r.rideId ?? "",
      totalPayableNu: centsToNu(r.total_payable_cents),
      driverPayoutNu: centsToNu(r.driver_payout_cents),
      platformFeeNu: centsToNu(r.platform_fee_cents),
      gstNu: centsToNu(r.gst_cents),
      platformFeeRuleId: r.platform_fee_rule_id ?? "",
      taxRuleId: r.tax_rule_id ?? "",
      createdAt: createdAt ? String(createdAt) : "",
      createdAtMs,
    };
  });
}

function normalizeGstMonthly(raw) {
  // { ok:true, data:[{month,total_rides,platform_fee_nu,gst_nu}] }
  const arr =
    (Array.isArray(raw?.data) && raw.data) ||
    (Array.isArray(raw?.items) && raw.items) ||
    (Array.isArray(raw) && raw) ||
    [];

  return arr.map((x, i) => ({
    id: `${x.month || "m"}-${i}`,
    month: String(x.month || ""),
    totalRides: Number(x.total_rides ?? 0),
    platformFeeNu: Number(x.platform_fee_nu ?? 0),
    gstNu: Number(x.gst_nu ?? 0),
  }));
}

export default function FinanceReports() {
  useIdleLogout();

  const def = useMemo(() => getDefaultRange(), []);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(def.from);
  const [toDate, setToDate] = useState(def.to);

  const [finRows, setFinRows] = useState([]);
  const [gstMonthly, setGstMonthly] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchFinanceSummary = async (token, from, to) => {
    if (!API_FIN_SUMMARY) {
      setFinRows([]);
      setError("Missing API config: set REACT_APP_TRANSPORT_FINANCE_SUMMARY_API_URL in .env and restart.");
      return;
    }

    const url = `${API_FIN_SUMMARY}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    const res = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`Failed to fetch finance summary (${res.status}) ${t.slice(0, 200)}`);
    }

    const json = await res.json();
    setFinRows(normalizeFinanceRows(json));
  };

  const fetchGstReport = async (token, from, to) => {
    if (!API_GST_REPORT) {
      setGstMonthly([]);
      return;
    }

    const res = await fetch(`${API_GST_REPORT}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!res.ok) {
      setGstMonthly([]);
      return;
    }

    const json = await res.json();
    setGstMonthly(normalizeGstMonthly(json));
  };

  const fetchAll = async () => {
    if (!fromDate || !toDate) {
      setError("Please select both start and end dates.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token") || "";
      await Promise.all([fetchFinanceSummary(token, fromDate, toDate), fetchGstReport(token, fromDate, toDate)]);
    } catch (e) {
      console.error("[FinanceReports] fetch error:", e);
      setError(e?.message || String(e));
      setFinRows([]);
      setGstMonthly([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchAll, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  // ✅ full filtered data (THIS is what export uses)
  const filteredAll = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromMs = fromDate ? new Date(fromDate).setHours(0, 0, 0, 0) : null;
    const toMs = toDate ? new Date(toDate).setHours(23, 59, 59, 999) : null;

    return finRows.filter((r) => {
      if (fromMs && r.createdAtMs && r.createdAtMs < fromMs) return false;
      if (toMs && r.createdAtMs && r.createdAtMs > toMs) return false;

      if (!q) return true;

      const combined =
        `${r.rideId} ${r.totalPayableNu} ${r.driverPayoutNu} ${r.platformFeeNu} ${r.gstNu} ${r.platformFeeRuleId} ${r.taxRuleId} ${r.createdAt}`.toLowerCase();

      return combined.includes(q);
    });
  }, [finRows, search, fromDate, toDate]);

  // ✅ only show 20 rows in UI
  const visibleRows = useMemo(() => filteredAll.slice(0, DISPLAY_LIMIT), [filteredAll]);

  const selectedGstRow = useMemo(() => {
    if (!gstMonthly.length) return null;
    const m = fromDate && /^\d{4}-\d{2}-\d{2}$/.test(fromDate) ? fromDate.slice(0, 7) : "";
    return (m && gstMonthly.find((x) => x.month === m)) || gstMonthly[0] || null;
  }, [gstMonthly, fromDate]);

  const totals = useMemo(() => {
    let gross = 0;
    let payout = 0;
    let fee = 0;
    let gst = 0;

    filteredAll.forEach((r) => {
      gross += r.totalPayableNu || 0;
      payout += r.driverPayoutNu || 0;
      fee += r.platformFeeNu || 0;
      gst += r.gstNu || 0;
    });

    return { gross, payout, fee, gst };
  }, [filteredAll]);

  const handleExport = () => {
    if (!filteredAll.length) return;

    const now = new Date();
    const exportedAt = now.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    // ✅ EXPORTS ALL FILTERED ROWS (not limited to 20)
    const data = [
      ["Transport Finance Summary"],
      [`Exported: ${exportedAt} (range: ${fromDate || "—"} to ${toDate || "—"})`],
      [],
      [
        "SI.NO",
        "RIDE ID",
        "TOTAL PAYABLE (Nu)",
        "DRIVER PAYOUT (Nu)",
        "PLATFORM FEE (Nu)",
        "GST (Nu)",
        "PLATFORM FEE RULE",
        "TAX RULE",
        "CREATED AT",
      ],
      ...filteredAll.map((r, idx) => [
        idx + 1,
        r.rideId || "—",
        Number(r.totalPayableNu ?? 0),
        Number(r.driverPayoutNu ?? 0),
        Number(r.platformFeeNu ?? 0),
        Number(r.gstNu ?? 0),
        r.platformFeeRuleId ?? "—",
        r.taxRuleId ?? "—",
        r.createdAtMs ? new Date(r.createdAtMs).toLocaleString() : "—",
      ]),
      [],
      ["", "TOTALS", totals.gross, totals.payout, totals.fee, totals.gst, "", "", ""],
    ];

    const ws = XLSX.utils.aoa_to_sheet(data);
    ws["!cols"] = [
      { wch: 8 },
      { wch: 10 },
      { wch: 18 },
      { wch: 18 },
      { wch: 16 },
      { wch: 10 },
      { wch: 16 },
      { wch: 10 },
      { wch: 22 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Finance Summary");
    XLSX.writeFile(wb, `transport-finance-summary_${fromDate}_to_${toDate}.xlsx`);
  };

  return (
    <div className="finance-reports-page">
      <div className="finance-reports-card">
        {/* ✅ ONLY KEEP THE 3 PILLS — REMOVE Current Balance / BTN COMPLETELY */}
        {selectedGstRow && (
          <div className="fr-gst-hint">
            <span className="fr-gst-pill">
              Month rides: <b>{selectedGstRow.totalRides}</b>
            </span>
            <span className="fr-gst-pill">
              Month platform fee: <b>{money(selectedGstRow.platformFeeNu)}</b>
            </span>
            <span className="fr-gst-pill">
              Month GST: <b>{money(selectedGstRow.gstNu)}</b>
            </span>
          </div>
        )}

        {/* ===== TOOLBAR ===== */}
        <div className="finance-reports-toolbar">
          <div className="finance-reports-toolbar-left">
            <div className="finance-reports-search">
              <span className="finance-reports-search-icon bi bi-search" aria-hidden="true" />
              <input
                type="text"
                className="finance-reports-input finance-reports-input-search"
                placeholder="Search ride id, rule id, amounts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="finance-reports-toolbar-right">
            <div className="finance-reports-date-pill">
              <input
                type="date"
                className="finance-reports-input finance-reports-input-date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <span className="finance-reports-to-label">to</span>
            <div className="finance-reports-date-pill">
              <input
                type="date"
                className="finance-reports-input finance-reports-input-date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <button
              type="button"
              className="finance-reports-export-btn"
              onClick={handleExport}
              disabled={!filteredAll.length}
              title={!filteredAll.length ? "No data to export" : "Export Excel (all rows)"}
            >
              Export Excel
            </button>
          </div>
        </div>

        {error && <div className="alert alert-danger mb-2">{error}</div>}



        {/* ===== TABLE ===== */}
        <div className="finance-reports-table-wrapper">
          <table className="finance-reports-table">
            <thead>
              <tr>
                <th className="col-si">SI.NO</th>
                <th className="col-month">RIDE ID</th>
                <th className="col-rides">TOTAL PAYABLE</th>
                <th className="col-fee">DRIVER PAYOUT</th>
                <th className="col-fee">PLATFORM FEE</th>
                <th className="col-gst">GST</th>
                <th className="col-rule">FEE RULE</th>
                <th className="col-rule">TAX RULE</th>
                <th className="col-date">CREATED AT</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="finance-reports-loading">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && !visibleRows.length && (
                <tr>
                  <td colSpan={9} className="finance-reports-empty">
                    No data found for the current filters.
                  </td>
                </tr>
              )}

              {!loading &&
                visibleRows.map((r, idx) => (
                  <tr key={r.id || idx}>
                    <td>{idx + 1}</td>
                    <td className="mono">{r.rideId || "—"}</td>
                    <td className="mono">{money(r.totalPayableNu)}</td>
                    <td className="mono">{money(r.driverPayoutNu)}</td>
                    <td className="mono">{money(r.platformFeeNu)}</td>
                    <td className="mono">{money(r.gstNu)}</td>
                    <td className="mono">{r.platformFeeRuleId || "—"}</td>
                    <td className="mono">{r.taxRuleId || "—"}</td>
                    <td>
                      <small className="text-muted">
                        {r.createdAtMs ? new Date(r.createdAtMs).toLocaleString() : "—"}
                      </small>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <footer className="dashboard-footer">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ།</strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}
